import {
  afterRenderEffect,
  AfterViewInit,
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { DatePipe } from '@angular/common';
import {
  FullCalendarComponent,
  FullCalendarModule,
} from '@fullcalendar/angular';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzPopoverDirective } from 'ng-zorro-antd/popover';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  CalendarOptions,
  EventChangeArg,
  EventInput,
} from '@fullcalendar/core';
import kaLocale from '@fullcalendar/core/locales/ka';
import '@fullcalendar/resource';
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import resourceScrollGridPlugin from '@fullcalendar/scrollgrid';
import momentPlugin from '@fullcalendar/moment';
import momentTimezonePlugin from '@fullcalendar/moment-timezone';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import adaptivePlugin from '@fullcalendar/adaptive';
import { AuthState } from '@beautyapp/auth';
import { Store } from '@ngxs/store';
import { NZ_DRAWER_DATA, NzDrawerRef, NzDrawerService } from 'ng-zorro-antd/drawer';
import {
  AvailabilityConflict,
  AvailabilityResponse,
  BookingService,
  CheckEmployeeAvailabilityRequest,
  EmployeeService,
  transformDateTime,
  VisitService,
} from '@beautyapp/core';
import { format } from 'date-fns';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { FormsModule } from '@angular/forms';
import { lastValueFrom, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { mapEmployeeSchedulesToEvents } from '../../../utils/schedule-mapper';
import { scheduleBackgroundEvents } from '../../../utils/schedule-background';
import { GetEvents } from '@beautyapp/appointment';
import { NzSpaceComponent, NzSpaceItemDirective } from 'ng-zorro-antd/space';
import { NzSelectComponent, NzSelectModule } from 'ng-zorro-antd/select';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { DatepickerComponent } from '@beautyapp/form';

@Component({
  selector: 'beautyapp-change-schedule',
  imports: [
    DatePipe,
    FullCalendarModule,
    NzColDirective,
    NzPopoverDirective,
    NzRowDirective,
    TranslatePipe,
    DatepickerComponent,
    NzButtonModule,
    FormsModule,
    NzIconModule,
    NzSpaceComponent,
    NzSpaceItemDirective,
    NzSelectModule,
    NzAlertModule,
  ],
  templateUrl: './change-schedule.component.html',
  styleUrl: './change-schedule.component.scss',
})
export class ChangeScheduleComponent implements OnInit, AfterViewInit {
  store = inject(Store);
  data = inject(NZ_DRAWER_DATA);
  drawerRef = inject(NzDrawerRef);
  visitService = inject(VisitService);
  employeeService = inject(EmployeeService);
  bookingService = inject(BookingService);
  translateService = inject(TranslateService);
  notificationService = inject(NzNotificationService);
  drawerService = inject(NzDrawerService);

  events = signal<any[]>([]);
  scheduleEvents = signal<any[]>([]);
  currentDate = signal<Date>(new Date());

  calendarEvents = signal<EventInput[]>([]);

  /**
   * The reassign picker. `[ngModel]="null"` asks it to snap back to the
   * placeholder after each pick, but Angular writes a bound input only when the
   * expression's value changes and a literal `null` never does, so the select
   * went on displaying whoever was picked — carrying the "გრაფიკი არ აქვს" that
   * was true at pick time and false the moment a roster was entered.
   */
  employeePicker = viewChild(NzSelectComponent);

  // Employee selector
  allEmployees = signal<any[]>([]);
  loadedEmployeeIds = signal<Set<string>>(new Set());
  originalEmployeeIds = signal<Set<string>>(new Set());

  saving = signal(false);

  // beaut-388: this reschedule/transfer surface had no day-off guard (unlike
  // add-booking / edit-booking-info), so a booking could be silently moved onto
  // an employee who isn't working that date. These mirror the sibling flow.
  availabilityConflicts = signal<AvailabilityConflict[]>([]);
  isCheckingAvailability = signal(false);
  proceedDespiteConflicts = signal(false);
  hasAvailabilityConflicts = computed(
    () => this.availabilityConflicts().length > 0
  );

  calendarComponent = viewChild<FullCalendarComponent>('calendar');
  timeInterval?: string = '00:05:00';
  calendarOptions: CalendarOptions = {
    schedulerLicenseKey: 'GPL-My-Project-Is-Open-Source',
    locales: [kaLocale],
    locale: 'ka',
    timeZone: 'Asia/Tbilisi',
    plugins: [
      resourceTimeGridPlugin,
      resourceTimelinePlugin,
      resourceScrollGridPlugin,
      momentPlugin,
      momentTimezonePlugin,
      interactionPlugin,
      timeGridPlugin,
      adaptivePlugin,
    ],
    initialView: 'resourceTimeGridDay', //'resourceTimeline', resourceTimeGridDay
    editable: true,
    weekends: true,
    selectable: true,
    dayMinWidth: 250,
    nowIndicator: true,
    height: '100%',
    eventClick: this.handleEventClick.bind(this),
    dateClick: this.handleDateClick.bind(this),
    eventChange: (info: EventChangeArg) => {
      const event = info.event;

      if (!event) {
        console.error('Event is undefined or null:', info);
        return;
      }

      if (!event.start || !event.end) {
        console.error('Event start or end time is missing:', event);
        return;
      }

      const start = event.start
        ? format(event.start, 'yyyy-MM-dd HH:mm')
        : null;
      const end = event.end ? format(event.end, 'yyyy-MM-dd HH:mm') : null;

      // find event in the events array and update it
      const updatedEvent = this.events().find((e) => e.id === event.id);
      console.log('Updated event:', updatedEvent);
      if (updatedEvent) {
        updatedEvent.start = start;
        updatedEvent.end = end;
        updatedEvent.resourceId = (event as any)?._def?.['resourceIds']?.[0];
        updatedEvent.title = `${format(event.start, 'HH:mm')} - ${format(
          event.end,
          'HH:mm'
        )}`;

        console.log('Updating event in the events array:', updatedEvent);
        this.events.update((events) => {
          const index = events.findIndex((e) => e.id === event.id);
          if (index !== -1) {
            events[index] = updatedEvent;
          }
          return [...events];
        });

        // beaut-388: a drag/move changed a target employee or time, so any prior
        // availability check + acknowledgement is stale.
        this.resetAvailabilityCheck();

        this.calendarComponent()?.getApi()?.render();
      } else {
        console.error('Event not found in the events array:', event.id);
      }
    },
    slotLabelInterval: '01:00',
    slotDuration: this.timeInterval,
    headerToolbar: false,
    titleFormat: 'D MMMM, YYYY, dddd',
    resourceOrder: 'calendarOrder',
    select: this.handleDateSelect.bind(this),
    eventTimeFormat: {
      hour: '2-digit',
      minute: '2-digit',
      omitZeroMinute: false,
      hour12: false,
    },
    slotLabelFormat: [
      {
        hour: '2-digit',
        minute: '2-digit',
        omitZeroMinute: false,
        meridiem: false,
        hour12: false,
      },
    ],
    allDaySlot: false,
    slotMinTime: '06:00:00',
    slotMaxTime: '24:00:00',
    navLinks: true,
    lazyFetching: true,
    resourceAreaColumns: [
      {
        field: 'title',
        headerContent: 'თანამშრომლები',
      },
    ],
  };

  constructor() {
    afterRenderEffect(() => {
      console.log(
        'ChangeScheduleComponent afterRenderEffect called',
        this.data.bookings
      );
      if (this.calendarEvents().length && this.events().length) {
      }
    });

    effect(() => {
      if (this.scheduleEvents().length) {
        console.log('Schedule events updated:', this.scheduleEvents());
        // this.calendarComponent()?.getApi()?.setOption('events', this.scheduleEvents());
        this.calendarComponent()
          ?.getApi()
          ?.addEventSource([...this.scheduleEvents()]);
      }
    });
  }

  ngOnInit() {
    // Load employees working on current date for dropdown
    this.loadWorkingEmployees();

    this.store.select(AuthState.getActiveBranch).subscribe((res: any) => {
      const startTime = `0${res.whStart}`.slice(-2);
      const endTime = `0${res.whEnd}`.slice(-2);
      this.calendarOptions.slotMinTime = `${startTime}:00:00`;
      this.calendarOptions.slotMaxTime = `${endTime}:00:00`;
    });

    if (this.data.bookings) {
      const employees = this.data.bookings.map((booking: any) => {
        return {
          id: booking.employee.id,
          title: booking.employee.user.fullName,
          userId: booking.employee.userId,
          icon: 'fa fa-user',
        };
      });

      console.log(employees);

      (this.calendarComponent()?.getApi() as any)?.setOption(
        'resources',
        employees
      );

      this.calendarComponent()?.getApi()?.render();
    }

    // this.store.select(AppointmentState.getCalendarEmployees)
    //   .subscribe((employees) => {
    //     (this.calendarComponent()
    //       ?.getApi() as any)
    //       ?.setOption('resources', employees);
    //   });

    // this.store.select(AppointmentState.getEvents)
    //   .subscribe((res: EventInput[]) => {
    //     console.log(res);
    //     if (res) {
    //       this.calendarComponent()?.getApi()?.setOption('events', res);
    //     }
    //   });
  }

  ngAfterViewInit() {
    console.log('ChangeScheduleComponent initialized with data:', this.data);
    if (this.data.visitDate) {
      this.currentDate.set(new Date(this.data.visitDate));
      const calendarApi = this.calendarComponent()?.getApi();
      if (calendarApi) {
        calendarApi.gotoDate(new Date(this.data.visitDate));
      }
    }
    if (this.data.bookings) {
      this.store.dispatch(
        new GetEvents({ date: format(this.currentDate(), 'yyyy-MM-dd') })
      );
      this.getEmployees();
      this.getCalendarEvents();

      // Track original employee IDs (cannot be removed)
      const origIds = new Set<string>(
        this.data.bookings.map((b: any) => b.employee.id)
      );
      this.originalEmployeeIds.set(origIds);

      const employees = this.data.bookings.map((booking: any) => {
        return {
          id: booking.employee.id,
          title: booking.employee.user.fullName,
          userId: booking.employee.userId,
          icon: 'fa fa-user',
        };
      });

      (this.calendarComponent()?.getApi() as any)?.setOption(
        'resources',
        employees
      );

      console.log(this.data.bookings);
      const events = this.data.bookings.map((m: any) => {
        console.log('Mapping booking:', m);
        const backgroundColor = m.visitId
          ? m.bookingServices && m.bookingServices.length
            ? m.bookingServices[0]?.service?.serviceCategory?.color
            : null
          : m.service?.serviceCategory?.color || null;

        const services = m.visitId
          ? m.bookingServices?.length
            ? m.bookingServices.map((s: any) => s.service?.name).join(', ')
            : ''
          : m.services?.map((s: any) => s.service?.name).join(', ');

        console.log(
          'Event services:',
          services,
          m.bookingServices,
          m.services,
          backgroundColor
        );
        return {
          id: m.id,
          resourceId: m.employee.id,
          start: transformDateTime(m.bookingDate, m.startTime),
          end: transformDateTime(m.bookingDate, m.endTime),
          title: m.startTime + ' - ' + m.endTime,
          backgroundColor: backgroundColor,
          groupId: m.id,
          oldStyle: !!m.visitId,
          extendedProps: {
            bookingDate: m.bookingDate,
            employeeId: m.employee.id,
            employeeName: m.employee.user.fullName,
            fullName: m.user?.fullName,
            userId: m.user?.id,
            mobilePhone: m.user?.mobilePhone,
            status: m.status,
            onUserRequest: m.onUserRequest,
            price: m.price,
            salePrice: m.salePrice,
            description: m.description,
            createdAt: m.createdAt,
            createdBy: m.createdBy?.fullName,
            completedBy: m.completedBy?.fullName,
            isAnonymous: m.isAnonymous,
            anonymousName: m.anonymousName + '(უცნობი)',
            services: services,
          },
        };
      });

      // Add the non-working events to the calendar
      this.events.set([...events]);
      const claendarEvnts = this.calendarEvents()
        .filter((event: any) => {
          return event.groupId && event.groupId !== this.data.visitId;
        })
        .map((event: any) => {
          return {
            ...event,
            display: 'background',
            backgroundColor: '#888888',
          };
        });

      console.log('Calendar events:', claendarEvnts);

      this.calendarComponent()
        ?.getApi()
        ?.setOption('events', [...claendarEvnts, ...events]);
      this.calendarComponent()?.getApi()?.render();

      console.log(
        'Calendar events:',
        this.calendarComponent()?.getApi()?.getEvents()
      );

      // Focus on specific booking if requested
      if (this.data.focusBookingId) {
        setTimeout(() => {
          const calendarApi = this.calendarComponent()?.getApi();
          const focusEvent = calendarApi?.getEventById(
            this.data.focusBookingId
          );
          if (focusEvent?.start) {
            calendarApi?.scrollToTime(format(focusEvent.start, 'HH:mm:ss'));
          }
        }, 500);
      }
    }
  }

  /**
   * beaut-546. Every column on the grid: the booking's own employees plus the
   * ones added through the reassign picker.
   *
   * Both per-date fetches take a LIST, and both already ran on every date
   * change — they just never knew about the added columns, which is why those
   * went blank on the next day. Widening the list they already send costs no
   * extra request; asking per employee instead would have meant two more
   * round trips for each one, every time the date moved.
   */
  private allColumnEmployeeIds(): string[] {
    const fromBookings = this.data.bookings.map((booking: any) => booking.employee.id);
    return [...new Set([...fromBookings, ...this.loadedEmployeeIds()])];
  }

  /**
   * Takes the roster paint off the grid so `getEmployees()` can lay a fresh one
   * down.
   *
   * Every load here goes in through `addEventSource`, which only appends, so
   * without this the band drawn for an employee with no roster survives the
   * refresh and sits on top of the shift that was just entered. Bookings are
   * left where they are — the visit being moved is an event on this calendar
   * too, and `getCalendarEvents()` re-reads them anyway.
   */
  clearScheduleBackground() {
    const calendarApi = this.calendarComponent()?.getApi();
    if (!calendarApi) return;

    scheduleBackgroundEvents(calendarApi.getEvents()).forEach((event: any) =>
      event.remove(),
    );
  }

  getEmployees() {
    const employeeIds = this.allColumnEmployeeIds();
    this.employeeService
      .getEmployeesSchedule({
        employeeIds: employeeIds,
        date: format(this.currentDate(), 'yyyy-MM-dd'),
      })
      .pipe(
        map((i: any) => {
          return i.map((m: any) => {
            return {
              id: m.id,
              title: m.fullName,
              userId: m.userId,
              icon: 'fa fa-user',
              schedule: m.schedule,
            };
          });
        })
      )
      .subscribe((res: any) => {
        console.log('Employees:', res);
        // this.calendarComponent()?.getApi()?.setOption('resources', res);
        const events = mapEmployeeSchedulesToEvents(
          res,
          format(this.currentDate(), 'yyyy-MM-dd'),
        );
        console.log('Mapped events:', events);
        const employeeIdsWithSchedules = new Set(
          events
            .filter((schedule: any) => schedule['resourceId']) // Filter out schedules without resourceId
            .map((schedule: any) => schedule['resourceId'])
        );
        console.log(
          'Employee IDs with schedules:',
          Array.from(employeeIdsWithSchedules)
        );
        const employeesWithoutSchedules = employeeIds.filter(
          (id: string) => !employeeIdsWithSchedules.has(id)
        );
        console.log('Employees without schedules:', employeesWithoutSchedules);

        // Create non-working day events for employees without schedules
        const nonWorkingEvents = employeesWithoutSchedules.map(
          (employeeId: any) => {
            // Find the employee details. beaut-546: an added column is not in
            // `bookings`, so fall back to the roster — and read the name
            // defensively, because this used to reach into `employee.user`
            // when `employee` could only ever be found.
            const employee = this.data.bookings.find(
              (booking: any) => booking.employee.id === employeeId
            )?.employee;
            const rosterEntry = this.allEmployees().find((e: any) => e.id === employeeId);

            // Get the current date from the calendar
            const currentDate =
              this.calendarComponent()?.getApi()?.getDate() || new Date();
            const formattedDate = format(currentDate, 'yyyy-MM-dd');

            // Create an all-day non-working event
            return {
              id: `off-after-${employeeId}`,
              resourceId: employeeId,
              start: `${formattedDate}T${this.calendarOptions.slotMinTime}`,
              end: `${formattedDate}T${this.calendarOptions.slotMaxTime}`,
              display: 'background',
              title: 'დასვენების დღე',
              backgroundColor: '#ff6d6d',
              borderColor: '#e41e1e',
              extendedProps: {
                type: 'day-off',
                position: 'after',
                employeeName:
                  employee?.user?.fullName || rosterEntry?.fullName || 'Unknown',
                bookingDate: formattedDate,
                color: '#e41e1e',
              },
            };
          }
        );

        events.push(...nonWorkingEvents);
        this.scheduleEvents.set(events);
      });
  }

  getCalendarEvents() {
    const employeeIds = this.allColumnEmployeeIds();
    const bookingIds = this.data.bookings.map((booking: any) => booking.id);

    this.bookingService
      .getBookingsByEmployeeIds(this.currentDate(), employeeIds)
      .pipe(
        map((res: any) => {
          return res
            .filter((m: any) => {
              // Filter out events that are not in the bookingIds
              return !bookingIds.includes(m.id);
            })
            .map((m: any) => {
              const backgroundColor = m.visitId
                ? m.bookingServices && m.bookingServices.length
                  ? m.bookingServices[0]?.service?.serviceCategory?.color
                  : null
                : m.service?.serviceCategory?.color || null;

              const services = m.visitId
                ? m.bookingServices?.length
                  ? m.bookingServices
                      .map((s: any) => s.service?.name)
                      .join(', ')
                  : ''
                : m.services?.map((s: any) => s.service?.name).join(', ');
              return {
                id: m.id,
                resourceId: m.employee.id,
                start: transformDateTime(this.currentDate(), m.startTime),
                display: 'background',
                end: transformDateTime(this.currentDate(), m.endTime),
                title: m.startTime + ' - ' + m.endTime,
                backgroundColor: backgroundColor,
                groupId: m.visitId,
                oldStyle: !!m.visitId,
                extendedProps: {
                  bookingDate: this.currentDate(),
                  employeeId: m.employee.id,
                  employeeName: m.employee.user.fullName,
                  fullName: m.user?.fullName,
                  userId: m.user?.id,
                  mobilePhone: m.user?.mobilePhone,
                  status: m.status,
                  onUserRequest: m.onUserRequest,
                  price: m.price,
                  salePrice: m.salePrice,
                  description: m.description,
                  createdAt: m.createdAt,
                  createdBy: m.createdBy?.fullName,
                  completedBy: m.completedBy?.fullName,
                  isAnonymous: m.isAnonymous,
                  anonymousName: m.anonymousName + '(უცნობი)',
                  services: services,
                },
              };
            });
        })
      )
      .subscribe((events) => {
        console.log('Fetched calendar events:', events);
        this.calendarEvents.set(events);
        this.calendarComponent()?.getApi()?.addEventSource(events);
      });
  }

  handleEventClick(event: any) {}

  handleDateClick(event: any) {}

  handleDateSelect(event: any) {}

  /**
   * beaut-546. Asks for the unscheduled employees too.
   *
   * This list is how a booking gets moved to someone else, and it used to hold
   * only the people with a roster row for the date. On stage one branch has 48
   * employees in the calendar and between 3 and 9 rostered on any given day —
   * with not a single DAY_OFF, VACATION or SICK_LEAVE row anywhere. So the
   * other 40-odd were not off; their roster was simply never filled in, and
   * they could not be chosen at all. When nobody was rostered the dropdown came
   * back empty, which is the "the employee list does not come up" on the card.
   *
   * They now appear in their own group, marked, and picking one still works:
   * the column is added as a full non-working day and the booking can be
   * dragged onto it. `openEmployeeSchedule` is the way to fix the cause rather
   * than work around it.
   */
  loadWorkingEmployees() {
    this.employeeService
      .getEmployeeToCalendar(format(this.currentDate(), 'yyyy-MM-dd'), true)
      .subscribe((employees: any[]) => {
        this.allEmployees.set(employees || []);
      });
  }

  /** Employees rostered for the day being shown. */
  scheduledEmployees = computed(() =>
    this.allEmployees().filter((e: any) => e?.schedule),
  );

  /** Employees with no roster row for that date — selectable, but flagged. */
  unscheduledEmployees = computed(() =>
    this.allEmployees().filter((e: any) => !e?.schedule),
  );

  /**
   * Opens the employee's own card so the roster can be filled in, then reloads
   * the day on close — otherwise the operator fixes the schedule and both the
   * dropdown and the grid still show the person as unscheduled until the page
   * is reloaded.
   */
  async openEmployeeSchedule(employee: any) {
    // Imported here rather than at the top on purpose. A static
    // `@beautyapp/hr` import puts that library in the module graph of every
    // spec under visit-modal/shared, and something it pulls in uses
    // `import.meta`, which Jest cannot parse — five suites stopped running the
    // moment it was added at the top of this file. Loading it at the click
    // keeps the graph as it was; the drawer only needs the class by then.
    const { EmployeeViewComponent, EMPLOYEE_CALENDAR_TAB_INDEX } = await import(
      '@beautyapp/hr'
    );

    const drawerRef = this.drawerService.create({
      nzTitle: employee?.fullName,
      nzContent: EmployeeViewComponent,
      nzWrapClassName: 'user-drawer',
      nzBodyStyle: { padding: '0 15px 15px 15px' },
      // Straight to the calendar: the operator was sent here to enter a
      // roster, and landing on the information tab makes them hunt for it.
      nzData: {
        employeeId: employee?.id,
        initialTabIndex: EMPLOYEE_CALENDAR_TAB_INDEX,
      },
    });

    drawerRef.afterClose.subscribe(() => {
      // All three, the way every other reload on this screen does it. Dropping
      // `getEmployees()` is what let the warning clear while the column stayed
      // red: the notice is read off `loadWorkingEmployees()`, but the roster is
      // painted only here, so the grid kept saying there was no schedule after
      // one had just been entered.
      this.loadWorkingEmployees();
      this.clearScheduleBackground();
      this.getEmployees();
      this.getCalendarEvents();
    });
  }

  /**
   * beaut-546. An added employee with no roster for the date being shown, so
   * the screen can offer to go and enter one.
   *
   * Derived rather than set when the column is added: the date can change
   * afterwards, and the same person may be rostered on one day and not the
   * next. Reading it from the roster means changing the date recomputes it,
   * and filling the schedule in clears it, with nothing to remember to reset.
   */
  unscheduledNotice = computed(() => {
    const added = this.loadedEmployeeIds();
    return this.allEmployees().find((e: any) => added.has(e.id) && !e?.schedule) ?? null;
  });

  addEmployeeToCalendar(employee: any) {
    if (!employee) return;

    // Back to the placeholder. The column that appears is the record of who was
    // added; leaving the name in the box only preserves a label that was read
    // off the roster at pick time and stops being true as soon as it changes.
    this.employeePicker()?.writeValue(null);

    const calendarApi = this.calendarComponent()?.getApi();
    if (!calendarApi) return;

    // Check if already added. Picking the same person twice would otherwise
    // fetch their day again and push a second, identical event source.
    const existingResource = calendarApi.getResourceById(employee.id);
    if (existingResource) return;

    // beaut-388: a new target employee is now available, so any prior
    // availability check + acknowledgement is stale.
    this.resetAvailabilityCheck();

    // Add resource column
    calendarApi.addResource({ id: employee.id, title: employee.fullName });

    // Track loaded employee
    this.loadedEmployeeIds.update((ids) => {
      const newSet = new Set(ids);
      newSet.add(employee.id);
      return newSet;
    });

    // Load employee schedule
    this.employeeService
      .getEmployeesSchedule({
        employeeIds: [employee.id],
        date: format(this.currentDate(), 'yyyy-MM-dd'),
      })
      .pipe(
        map((i: any) =>
          i.map((m: any) => ({
            id: m.id,
            title: m.fullName,
            userId: m.userId,
            icon: 'fa fa-user',
            schedule: m.schedule,
          }))
        )
      )
      .subscribe((res: any) => {
        const events = mapEmployeeSchedulesToEvents(
          res,
          format(this.currentDate(), 'yyyy-MM-dd'),
        );
        // If no schedule found, add day-off event
        if (!events.some((e: any) => e.resourceId === employee.id)) {
          const formattedDate = format(this.currentDate(), 'yyyy-MM-dd');
          events.push({
            id: `off-after-${employee.id}`,
            resourceId: employee.id,
            start: `${formattedDate}T${this.calendarOptions.slotMinTime}`,
            end: `${formattedDate}T${this.calendarOptions.slotMaxTime}`,
            display: 'background',
            title: 'დასვენების დღე',
            backgroundColor: '#ff6d6d',
            borderColor: '#e41e1e',
            extendedProps: {
              type: 'day-off',
              employeeName: employee.fullName,
              bookingDate: formattedDate,
            },
          });
        }
        calendarApi.addEventSource(events);
      });

    // Load employee existing bookings
    this.bookingService
      .getBookingsByEmployeeIds(this.currentDate(), [employee.id])
      .pipe(
        map((res: any) =>
          res.map((m: any) => ({
            id: m.id,
            resourceId: employee.id,
            start: transformDateTime(this.currentDate(), m.startTime),
            end: transformDateTime(this.currentDate(), m.endTime),
            display: 'background',
            title: m.startTime + ' - ' + m.endTime,
            backgroundColor: '#888888',
            extendedProps: {
              employeeName: employee.fullName,
              fullName: m.user?.fullName,
              services: m.visitId
                ? m.bookingServices?.map((s: any) => s.service?.name).join(', ')
                : m.services?.map((s: any) => s.service?.name).join(', '),
            },
          }))
        )
      )
      .subscribe((events) => {
        calendarApi.addEventSource(events);
      });
  }

  removeEmployeeFromCalendar(employeeId: string) {
    const calendarApi = this.calendarComponent()?.getApi();
    if (!calendarApi) return;

    // Don't allow removing original booking employees
    if (this.originalEmployeeIds().has(employeeId)) return;

    // beaut-388: the set of target employees changed; drop any stale check.
    this.resetAvailabilityCheck();

    // Remove all events for this employee
    const allEvents = calendarApi.getEvents();
    allEvents.forEach((event: any) => {
      const resourceIds = event._def?.resourceIds || [];
      if (
        resourceIds.includes(employeeId) ||
        event.getResources?.()?.some((r: any) => r.id === employeeId)
      ) {
        event.remove();
      }
    });

    // Remove resource
    const resource = calendarApi.getResourceById(employeeId);
    if (resource) {
      resource.remove();
    }

    this.loadedEmployeeIds.update((ids) => {
      const newSet = new Set(ids);
      newSet.delete(employeeId);
      return newSet;
    });
  }

  isOriginalEmployee(employeeId: string): boolean {
    return this.originalEmployeeIds().has(employeeId);
  }

  // beaut-388: clear a previous check + acknowledgement whenever the target
  // employees or date change, so a stale "proceed anyway" can't skip the guard.
  private resetAvailabilityCheck(): void {
    this.availabilityConflicts.set([]);
    this.proceedDespiteConflicts.set(false);
  }

  // beaut-388: check every (booking -> target employee, time) pair against the
  // backend availability rules, so moving a booking onto a non-working employee
  // (no schedule / vacation / day-off) surfaces a warning instead of silently
  // saving an invalid booking. Returns true when there are no conflicts.
  async checkAllAvailability(): Promise<boolean> {
    const date = format(this.currentDate(), 'yyyy-MM-dd');
    const toCheck = this.events().filter(
      (e: any) => e.resourceId && e.start && e.end
    );
    if (!toCheck.length) return true;

    this.isCheckingAvailability.set(true);
    this.availabilityConflicts.set([]);
    try {
      const responses = await Promise.all(
        toCheck.map((e: any) => {
          const params: CheckEmployeeAvailabilityRequest = {
            employeeId: e.resourceId,
            date,
            startTime: format(new Date(e.start), 'HH:mm'),
            endTime: format(new Date(e.end), 'HH:mm'),
            excludeBookingId: e.id,
          };
          return lastValueFrom(
            this.bookingService.checkEmployeeAvailability(params).pipe(
              catchError((error) => {
                console.error('Availability check failed:', error);
                return of({
                  available: true,
                  conflicts: [],
                } as AvailabilityResponse);
              })
            )
          );
        })
      );
      const conflicts = responses.flatMap((r) => r.conflicts);
      this.availabilityConflicts.set(conflicts);
      return conflicts.length === 0;
    } finally {
      this.isCheckingAvailability.set(false);
    }
  }

  getConflictMessage(conflict: AvailabilityConflict): string {
    if (conflict.reason) {
      return `${conflict.name}: ${conflict.reason}`;
    }
    return conflict.name;
  }

  // beaut-388: acknowledge the warning and save anyway (mirrors the sibling
  // edit-booking-info flow).
  confirmDespiteConflicts(): void {
    this.proceedDespiteConflicts.set(true);
    this.changeSchedule();
  }

  async changeSchedule() {
    // beaut-388: gate the reschedule on the availability check unless the user
    // has already acknowledged the warning.
    if (!this.proceedDespiteConflicts()) {
      const available = await this.checkAllAvailability();
      if (!available) {
        return; // conflicts found — the warning renders; wait for the user.
      }
    }

    this.saving.set(true);
    this.visitService
      .rescheduleVisit({
        visitId: this.data.visitId,
        events: this.events().map((e: any) => ({
          id: e.id,
          bookingDate: format(this.currentDate(), 'yyyy-MM-dd'),
          start: e.start,
          end: e.end,
          employeeId: e.resourceId,
        })),
      })
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.drawerRef.close(true);
        },
        error: (err) => {
          this.saving.set(false);
          this.notificationService.error(
            'შეცდომა',
            err?.error?.message || 'შენახვა ვერ მოხერხდა'
          );
        },
      });
  }

  changeDate($event: Date) {
    this.currentDate.set($event);
    // beaut-388: a new date means new schedules; drop any stale check.
    this.resetAvailabilityCheck();
    const calendarApi = this.calendarComponent()?.getApi();
    if (calendarApi) {
      this.getEmployees();
      this.getCalendarEvents();
      this.loadWorkingEmployees();
      this.onDateChangeEvent($event);
      calendarApi.gotoDate($event);
      calendarApi.render();
    }
  }

  nextDate() {
    const calendarApi = this.calendarComponent()?.getApi();
    if (!calendarApi) {
      console.error('Calendar API is not available');
      return;
    }
    calendarApi.next();
    this.currentDate.set(new Date(calendarApi.getCurrentData().currentDate));
    this.getEmployees();
    this.getCalendarEvents();
    this.loadWorkingEmployees();
    this.onDateChangeEvent(this.currentDate());
  }

  prevDate() {
    const calendarApi = this.calendarComponent()?.getApi();
    if (!calendarApi) {
      console.error('Calendar API is not available');
      return;
    }
    calendarApi.prev();
    this.currentDate.set(new Date(calendarApi.getCurrentData().currentDate));
    this.getEmployees();
    this.getCalendarEvents();
    this.loadWorkingEmployees();
    this.onDateChangeEvent(this.currentDate());
  }

  currenDate() {
    const calendarApi = this.calendarComponent()?.getApi();

    if (!calendarApi) {
      console.error('Calendar API is not available');
      return;
    }

    calendarApi.gotoDate(new Date());

    this.currentDate.set(new Date());
    this.getEmployees();
    this.getCalendarEvents();
    this.loadWorkingEmployees();
    this.onDateChangeEvent(new Date());
  }

  onDateChangeEvent(date: Date) {
    console.log('onDateChangeEvent:', date);
    const events = this.data.bookings.map((m: any) => {
      const backgroundColor = m.visitId
        ? m.bookingServices && m.bookingServices.length
          ? m.bookingServices[0]?.service?.serviceCategory?.color
          : null
        : m.service?.serviceCategory?.color || null;

      const services = m.visitId
        ? m.bookingServices?.length
          ? m.bookingServices.map((s: any) => s.service?.name).join(', ')
          : ''
        : m.services?.map((s: any) => s.service?.name).join(', ');

      console.log(
        'Event services:',
        services,
        m.bookingServices,
        m.services,
        backgroundColor
      );
      return {
        id: m.id,
        resourceId: m.employee.id,
        start: transformDateTime(date, m.startTime),
        end: transformDateTime(date, m.endTime),
        title: m.startTime + ' - ' + m.endTime,
        backgroundColor: backgroundColor,
        groupId: m.id,
        oldStyle: !!m.visitId,
        extendedProps: {
          bookingDate: date,
          employeeId: m.employee.id,
          employeeName: m.employee.user.fullName,
          fullName: m.user?.fullName,
          userId: m.user?.id,
          mobilePhone: m.user?.mobilePhone,
          status: m.status,
          onUserRequest: m.onUserRequest,
          price: m.price,
          salePrice: m.salePrice,
          description: m.description,
          createdAt: m.createdAt,
          createdBy: m.createdBy?.fullName,
          completedBy: m.completedBy?.fullName,
          isAnonymous: m.isAnonymous,
          anonymousName: m.anonymousName + '(უცნობი)',
          services: services,
        },
      };
    });

    this.calendarComponent()
      ?.getApi()
      ?.setOption('events', [...events]);
    this.calendarComponent()?.getApi()?.render();
  }

  getEvents(date: Date) {
    const params = {
      date: format(date, 'yyyy-MM-dd'),
    };
    this.bookingService
      .getCalendarBookingNewV2(params)
      .pipe(
        map((res: any) => {
          return res.map((m: any) => {
            const backgroundColor =
              m.bookingService?.service?.serviceCategory?.color;

            const services = m.id
              ? m.bookingService?.service?.name
              : m.services?.map((s: any) => s.service?.name).join(', ');
            return {
              id: m.id,
              resourceId: m.employee.id,
              start: transformDateTime(date, m.startTime),
              end: transformDateTime(date, m.endTime),
              title: m.startTime + ' - ' + m.endTime,
              backgroundColor: backgroundColor,
              groupId: m.id,
              oldStyle: !!m.id,
              extendedProps: {
                bookingDate: date,
                employeeId: m.employee.id,
                employeeName: m.employee.user.fullName,
                fullName: m.user?.fullName,
                userId: m.user?.id,
                mobilePhone: m.user?.mobilePhone,
                status: m.status,
                onUserRequest: m.onUserRequest,
                price: m.price,
                salePrice: m.salePrice,
                description: m.description,
                createdAt: m.createdAt,
                createdBy: m.createdBy?.fullName,
                completedBy: m.completedBy?.fullName,
                isAnonymous: m.isAnonymous,
                anonymousName: m.anonymousName + '(უცნობი)',
                services: services,
              },
            };
          });
        })
      )
      .subscribe((events: any[]) => {
        console.log('Fetched events:', events);
        this.events.set(events);
        this.calendarComponent()?.getApi()?.addEventSource(events);
        this.calendarComponent()?.getApi()?.render();
      });
  }

  changeTimeInterval(timeInterval: string) {
    this.timeInterval = timeInterval;
    if (this.timeInterval === 'resourceTimeline') {
      this.calendarOptions = {
        ...this.calendarOptions,
      };
    }
    this.calendarOptions.slotDuration = timeInterval;
  }
}
